/* SPEC-506 — the last 10 failed/slow requests and a count of console errors by TYPE, kept in
 * memory for the Feedback panel's "technical details".
 *
 * Fix loop 2 (Codex): no free text is kept. A request is recorded as method / ROUTE TEMPLATE /
 * status / latency / request id — the path is mapped to one of the app's own route templates
 * (from /api/feedback/config) the moment the call finishes and the raw path is never stored; a
 * path that matches no template is not recorded. A console error is recorded as its type only
 * (`TypeError`, `ConsoleMessage`, …) with a count — never its message. Never a body or header.
 *
 * Disclosed to the reader: the panel lists what will be sent and offers "Don't include
 * technical details". */
import { apiCallRecord, countType, errorType, pushRing } from './core';

type ApiCall = NonNullable<ReturnType<typeof apiCallRecord>>;

let apiCalls: ApiCall[] = [];
let errorCounts: Array<{ type: string; count: number }> = [];
let routes: readonly string[] = [];
let installed = false;

/** The route templates calls are mapped to. Until they load, nothing is recorded. */
export function setRouteTemplates(list: readonly string[]): void {
  routes = list;
}

function recordError(type: string) {
  try { errorCounts = countType(errorCounts, type); } catch { /* never break console.error */ }
}

export function installFeedbackTelemetry(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const realFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const started = performance.now();
    const method = init?.method || (input instanceof Request ? input.method : 'GET');
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    try {
      const res = await realFetch(input, init);
      const rec = apiCallRecord(method, url, res.status, performance.now() - started,
        res.headers.get('x-request-id'), routes);
      if (rec) apiCalls = pushRing(apiCalls, rec);
      return res;
    } catch (err) {
      const rec = apiCallRecord(method, url, null, performance.now() - started, null, routes);
      if (rec) apiCalls = pushRing(apiCalls, rec);
      throw err;
    }
  };

  const realError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    recordError(errorType(args.find((a) => a instanceof Error) ?? args[0]));
    realError(...args);
  };
  window.addEventListener('error', (e) => recordError(e.error instanceof Error ? errorType(e.error) : 'ScriptError'));
  window.addEventListener('unhandledrejection', (e) =>
    recordError(e.reason instanceof Error ? errorType(e.reason) : 'UnhandledRejection'));
}

/** Forget everything recorded — on a change of login in the same tab. */
export function resetFeedbackTelemetry(): void {
  apiCalls = [];
  errorCounts = [];
}

export function recentApiCalls(): ApiCall[] {
  return [...apiCalls];
}

export function recentConsoleErrors(): Array<{ type: string; count: number }> {
  return errorCounts.map((c) => ({ ...c }));
}
